/**
 * PURPOSE:
 * Centralizar utilidades defensivas para pintar categorías y llaves públicas de Karate en Hayllis.
 *
 * RESPONSIBILITIES:
 * - Separar matches por bracket.
 * - Ordenar matches y categorías de forma estable.
 * - Resolver textos públicos de competidor, institución, score, ganador y estado.
 * - Evitar que la UI principal mezcle reglas de presentación con lectura de datos.
 *
 * COLLABORATORS:
 * - CategoriasKarate.jsx
 * - PublicKarateBracketTree.jsx
 * - PublicKarateMatchBox.jsx
 *
 * ROUTE:
 * src/components/Software/Karate/Categorias/utils/karateBracketUtils.js
 */

export const KARATE_HAYLLIS_EVENT_ID = 223;
export const KARATE_HAYLLIS_SPORT_ID = 10;

export const isKarateHayllisAllowed = ({ idevent, idsport }) => {
  return Number(idevent) === KARATE_HAYLLIS_EVENT_ID && Number(idsport) === KARATE_HAYLLIS_SPORT_ID;
};

export const getSafeMatches = (selectedCategoryDetail) => {
  return Array.isArray(selectedCategoryDetail?.allMatches)
    ? selectedCategoryDetail.allMatches
    : [];
};

export const filterMatchesByBracket = (matches, bracketName) => {
  return (Array.isArray(matches) ? matches : []).filter(
    (match) => match?.bracket?.name === bracketName,
  );
};

export const toComparableNumber = (value, fallback = Number.MAX_SAFE_INTEGER) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

export const sortMatchesByOperationalOrder = (matches = []) => {
  return [...matches].sort((a, b) => {
    const byRound = toComparableNumber(a?.round) - toComparableNumber(b?.round);
    if (byRound !== 0) return byRound;

    const byOrder = toComparableNumber(a?.matchOrder) - toComparableNumber(b?.matchOrder);
    if (byOrder !== 0) return byOrder;

    const byNumber = toComparableNumber(a?.matchNumber) - toComparableNumber(b?.matchNumber);
    if (byNumber !== 0) return byNumber;

    return toComparableNumber(a?.id) - toComparableNumber(b?.id);
  });
};

export const groupMatchesByRound = (matches = []) => {
  const map = sortMatchesByOperationalOrder(matches).reduce((acc, match) => {
    const round = toComparableNumber(match?.round, 0);
    (acc[round] ||= []).push(match);
    return acc;
  }, {});

  return Object.keys(map)
    .sort((a, b) => Number(a) - Number(b))
    .map((round) => ({
      n: Number(round),
      matches: sortMatchesByOperationalOrder(map[round]),
    }));
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const parseCategoryCodeForSort = (category) => {
  const rawCode = normalizeText(category?.code || category?.description || category?.id);
  const normalized = rawCode.toUpperCase();
  const match = normalized.match(/^([A-Z]+)\s*[-_]?\s*(\d+)?/);

  if (!match) {
    return {
      prefix: normalized || "ZZZ",
      number: Number.MAX_SAFE_INTEGER,
      raw: normalized,
    };
  }

  return {
    prefix: match[1] || "ZZZ",
    number: match[2] ? Number(match[2]) : Number.MAX_SAFE_INTEGER,
    raw: normalized,
  };
};

/** Orden natural: A1, A2... A10, luego B1, B2... B24. */
export const sortCategoriesByNaturalCode = (categories = []) => {
  return [...(Array.isArray(categories) ? categories : [])].sort((a, b) => {
    const left = parseCategoryCodeForSort(a);
    const right = parseCategoryCodeForSort(b);

    const byPrefix = left.prefix.localeCompare(right.prefix, "es", { sensitivity: "base" });
    if (byPrefix !== 0) return byPrefix;

    const byNumber = left.number - right.number;
    if (byNumber !== 0) return byNumber;

    const byRaw = left.raw.localeCompare(right.raw, "es", { numeric: true, sensitivity: "base" });
    if (byRaw !== 0) return byRaw;

    return toComparableNumber(a?.id) - toComparableNumber(b?.id);
  });
};

export const getCategoryTitle = (category) => {
  return category?.description || category?.code || `Categoría ${category?.id ?? ""}`.trim();
};

export const getCategorySubtitle = (category) => {
  const parts = [
    category?.code,
    category?.categoryType?.description || category?.categoryType?.name,
    category?.modality?.description || category?.modality?.name,
  ].filter(Boolean);

  return parts.join(" · ");
};

export const getCompetitorDisplayName = (categoryCompetitor) => {
  if (!categoryCompetitor) return "Pendiente";

  const fighterName = normalizeText(categoryCompetitor?.fighter?.name);
  if (fighterName) return fighterName;

  const participantName = normalizeText(
    `${categoryCompetitor?.participant?.name || ""} ${categoryCompetitor?.participant?.lastName || ""}`,
  );
  if (participantName) return participantName;

  const institutionName = normalizeText(
    categoryCompetitor?.institution?.description || categoryCompetitor?.institution?.name,
  );
  if (institutionName) return institutionName;

  return "Pendiente";
};

export const getCompetitorInstitution = (categoryCompetitor) => {
  if (!categoryCompetitor) return "—";

  return normalizeText(
    categoryCompetitor?.fighter?.institutionAbrev ||
      categoryCompetitor?.institution?.name ||
      categoryCompetitor?.participant?.institution?.name ||
      categoryCompetitor?.institution?.description ||
      categoryCompetitor?.participant?.institution?.description,
  ) || "—";
};

export const getMatchOrderLabel = (match) => {
  const matchOrder = Number(match?.matchOrder);
  return Number.isFinite(matchOrder) ? `${matchOrder}` : "—";
};

export const getWinnerSide = (match) => {
  if (!match?.winner) return null;

  if (typeof match.winner === "string") {
    const side = match.winner.toLowerCase();
    return side === "aka" || side === "ao" ? side : null;
  }

  if (match?.akaCategoryCompetitor?.id && match.winner?.id === match.akaCategoryCompetitor.id) {
    return "aka";
  }

  if (match?.aoCategoryCompetitor?.id && match.winner?.id === match.aoCategoryCompetitor.id) {
    return "ao";
  }

  return null;
};

export const hasScoreValue = (value) => {
  return value !== null && value !== undefined && String(value).trim() !== "";
};

export const getSideScore = (match, side) => {
  const value = side === "aka" ? match?.akaNumPoints : match?.aoNumPoints;
  return hasScoreValue(value) ? String(value) : "—";
};

export const getMatchStatusInfo = (match) => {
  const id = Number(match?.status?.id);
  const description = match?.status?.description || match?.status?.name;

  if (id === 3) {
    return {
      label: description || "Finalizado",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    };
  }

  if (id === 2) {
    return {
      label: description || "En curso",
      className: "border-amber-200 bg-amber-50 text-amber-700",
    };
  }

  return {
    label: description || "Pendiente",
    className: "border-slate-200 bg-slate-50 text-slate-600",
  };
};

export const getFinalizedCount = (matches = []) => {
  return (Array.isArray(matches) ? matches : []).filter((match) => Number(match?.status?.id) === 3).length;
};

export const formatLastUpdated = (value) => {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(value);
  } catch (error) {
    return "—";
  }
};
